/**
 * useTendrilFeedbackMutation — POST /api/tendril/feedback.
 *
 * Captures a thumbs-up/thumbs-down reaction on a Tendril assistant turn (see
 * `handleFeedback()` in backend/src/handlers/tendril.ts). Unlike the chat-log
 * writes on /chat and /confirm (fire-and-forget), FeedbackControls awaits
 * this mutation and only shows the "captured" state after it resolves 2xx —
 * so no query invalidation is needed here (no feedback list view exists to
 * refresh).
 */
import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { apiClient } from '../../api/client.js';

export interface TendrilFeedbackVars {
  conversationId: string;
  reaction: 'up' | 'down';
  /** Free-text reason for a thumbs-down. Omit (do not send empty string) when not provided. */
  reason?: string;
  turnIndex: number;
  assistantMessageExcerpt: string;
  /**
   * The turn's terminal outcome (tendril-streaming-responses Phase 3,
   * WI-008) — lets feedback analysis distinguish a thumbs-down on a fully
   * successful reply from one on a partial/degraded or error turn. Omit
   * (do not send undefined) when not provided, matching the `reason`
   * omission convention.
   */
  outcome?: 'ok' | 'degraded' | 'aborted' | 'error';
}

export interface TendrilFeedbackResponse {
  success: boolean;
  feedbackId: string;
}

export function useTendrilFeedbackMutation(): UseMutationResult<
  TendrilFeedbackResponse,
  Error,
  TendrilFeedbackVars
> {
  return useMutation<TendrilFeedbackResponse, Error, TendrilFeedbackVars>({
    mutationFn: async ({ conversationId, reaction, reason, turnIndex, assistantMessageExcerpt, outcome }) => {
      const body = {
        conversationId,
        reaction,
        ...(reason !== undefined && reason.length > 0 && { reason }),
        turnIndex,
        assistantMessageExcerpt,
        ...(outcome !== undefined && { outcome }),
      };
      const raw = await apiClient.post('/api/tendril/feedback', body);
      return raw as TendrilFeedbackResponse;
    },
  });
}
